<script setup>
  import { ref, computed, useSlots } from 'vue';

  import CredereLoading from '@/components/Credere/Loading.vue';

  const slots = useSlots();

  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },

    size: {
      type: String,
      default: 'medium',
      required: false,
      validator(value) {
        return ['small', 'medium', 'large'].includes(value);
      },
    },
    color: {
      type: String,
      default: 'primary',
      required: false,
      validator(value) {
        return ['primary', 'secondary', 'highlight', 'danger', 'light'].includes(value);
      },
    },

    href: {
      type: String,
      default: null,
      required: false,
    },

    variant: {
      type: String,
      default: 'A',
      required: false,
      validator(value) {
        return ['A', 'B', 'C'].includes(value);
      },
    },

    variantSecondary: {
      type: String,
      default: 'A',
      required: false,
      validator(value) {
        return ['A', 'B'].includes(value);
      },
    },

    icon: {
      type: String,
      required: false,
    },
    iconPosition: {
      type: String,
      default: 'left',
      required: false,
      validator(value) {
        return ['left', 'right'].includes(value);
      },
    },
    iconSize: {
      type: String,
      default: 'medium',
      required: false,
      validator(value) {
        return ['medium', 'large'].includes(value);
      },
    },
  });

  const emit = defineEmits(['click', 'blur', 'focus']);

  function click(e) {
    emit('click', e);
  }

  function focus(e) {
    emit('focus', e);
  }

  function blur(e) {
    emit('blur', e);
  }

  const { button_primary: _primary, button_secondary: _secondary } = window.CREDERE.variants || {};
  const variantPrimary = computed(() => _primary || props.variant);
  const variantSecondary = computed(() => _secondary || props.variantSecondary);

  const classes = computed(() => ({
    'credere-button': true,

    [`size--${props.size}`]: true,
    [`color--${props.color}`]: true,

    [`icon-size--${props.iconSize}`]: true,
    [`icon-position--${props.iconPosition}`]: true,

    'state--disabled': props.disabled,
    'state--loading': props.loading,

    'variant--iconed': props.icon && !slots.default,
    [`variant--button-1${variantPrimary.value?.toLowerCase()}`]: variantPrimary.value,
    [`variant--button-2${variantSecondary.value?.toLowerCase()}`]: props.color === 'secondary' && variantSecondary.value,
  }));

  const refButton = ref();
  defineExpose({ refButton });
</script>

<template>
  <component
    ref="refButton"

    :is="href  ? 'a' : 'button'"
    :class="classes"
    :disabled="disabled || loading"

    :href="href"

    @click="click"
    @blur="blur"
    @focus="focus"
  >
    <credere-loading
      v-if="loading"
      class="credere-button-loading"
      light
    />

    <font-awesome-icon
      v-if="icon"
      :icon="icon"
      class="credere-button-icon"
    />

    <slot />
  </component>
</template>

<style lang="scss">
  @import '@css/mixins/button.scss';

  .credere-button {
    @include credere-button;

    &.size--small {
      @include button-size(
        var(--token-font-size-s),
        var(--token-font-size-s),
        var(--token-spacing-micro) var(--token-spacing-xxxs),
        var(--token-spacing-nano),
      );
    }

    &.size--medium {
      @include button-size(
        var(--token-font-size-m),
        var(--token-font-size-s),
        var(--token-spacing-xxxs) var(--token-spacing-l),
        var(--token-spacing-xxxs),
      );
    }

    &.size--large {
      @include button-size(
        var(--token-font-size-l),
        var(--token-font-size-m),
        var(--token-spacing-xs) var(--token-spacing-giga),
        var(--token-spacing-xxxs),
      );
    }

    &.color--primary {
      @include button-color(
        var(--token-color-neutral-darker),
        var(--token-color-neutral-heavy),
        var(--token-color-constant-light),
      );
    }

    &.color--secondary {
      @include button-color(
        var(--token-color-neutral-dark),
        var(--token-color-constant-dark),
        var(--token-color-constant-light),
      );
    }

    &.color--highlight {
      @include button-color(
        var(--token-color-highlight-primary),
        var(--token-color-highlight-dark),
        var(--token-color-constant-dark),
      );
    }

    &.color--danger {
      @include button-color(
        var(--token-color-alert-danger),
        var(--token-color-alert-danger-dark),
        var(--token-color-constant-light),
      );
    }

    &.color--light {
      @include button-color(
        var(--token-color-neutral-light),
        var(--token-color-neutral-lighter),
        var(--token-color-constant-dark),
      );
    }

    &.icon-position--right {
      flex-direction: row-reverse;
    }

    &.icon-size--large {
      .credere-button-icon {
        font-size: 1.25em;
      }
    }
  }

  .credere-button-loading {
    bottom: 0;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
  }
</style>
